import { Fragment, useState } from 'react';
import { T, useGT } from 'gt-react';
import { DIVISION_PERCENT_NOT_APPLICABLE } from '@erp/rpc';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PercentInput } from './PercentInput';
import {
  PERCENT_PATTERN,
  divisionRows,
  hasPercent,
  isNotApplicable,
  type DivisionEntry,
  type DivisionRow,
  type PricelistDetailsFormValues,
} from './pricelistDetailsFormState';
import type { PricelistDetailsForm } from './usePricelistDetailsForm';

/**
 * The entry keys on screen: every division, plus the branches of the divisions
 * currently expanded. Select-all covers exactly these, so it never quietly
 * takes in rows the user can't see.
 */
function visibleEntryKeys(
  rows: DivisionRow[],
  expanded: ReadonlySet<string>,
): string[] {
  return rows.flatMap((row) => [
    row.entry.key,
    ...(expanded.has(row.division.id)
      ? row.branches.map((branch) => branch.key)
      : []),
  ]);
}

/** Divisions that already have a per-branch percentage saved. */
function rowsWithBranchPercents(
  rows: DivisionRow[],
  values: PricelistDetailsFormValues,
): string[] {
  const ids: string[] = [];
  for (const row of rows) {
    if (row.branches.some((branch) => hasPercent(values, branch.key))) {
      ids.push(row.division.id);
    }
  }
  return ids;
}

/** The set with `key` added or removed. */
function withKey(
  keys: ReadonlySet<string>,
  key: string,
  present: boolean,
): Set<string> {
  const next = new Set(keys);
  if (present) next.add(key);
  else next.delete(key);
  return next;
}

/**
 * Set one percentage across every selected row.
 *
 * The reason this table exists: a price change usually lands on many divisions
 * at the same number, and typing it into each field in turn was the slow part.
 */
function BulkApplyBar({
  count,
  percent,
  disabled,
  onPercentChange,
  onApply,
  onMarkNotApplicable,
  onClear,
  onDeselect,
}: {
  count: number;
  percent: string;
  disabled: boolean;
  onPercentChange: (percent: string) => void;
  onApply: () => void;
  onMarkNotApplicable: () => void;
  onClear: () => void;
  onDeselect: () => void;
}) {
  const gt = useGT();
  const typed = percent.trim();
  const canApply = PERCENT_PATTERN.test(typed);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5">
      <span className="text-sm font-medium">
        {gt('{count} selected', {
          context: 'count of selected division rows in the price change table',
          count: String(count),
        })}
      </span>
      <PercentInput
        ariaLabel={gt('Percentage to apply to the selected rows', {
          context: 'aria label for the bulk percentage input',
        })}
        value={percent}
        invalid={typed !== '' && !canApply}
        disabled={disabled}
        className="w-24"
        onChange={onPercentChange}
      />
      <Button size="sm" disabled={disabled || !canApply} onClick={onApply}>
        <T context="button that writes the bulk percentage into every selected row">
          Apply to selected
        </T>
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={onMarkNotApplicable}
      >
        <T context="button that marks every selected division row as N/A: reviewed, and the price change does not apply to it">
          Mark N/A
        </T>
      </Button>
      <Button variant="outline" size="sm" disabled={disabled} onClick={onClear}>
        <T context="button that blanks the percentage of every selected row">
          Clear
        </T>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={onDeselect}
      >
        <T context="button that deselects every selected division row">
          Deselect
        </T>
      </Button>
    </div>
  );
}

/** The expand toggle in a division's Branches cell, or a dash when it has none. */
function BranchesCell({
  row,
  values,
  open,
  disabled,
  onToggle,
}: {
  row: DivisionRow;
  values: PricelistDetailsFormValues;
  open: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const gt = useGT();
  if (row.branches.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const filled = row.branches.filter((branch) =>
    hasPercent(values, branch.key),
  ).length;
  const total = String(row.branches.length);

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 gap-1 px-2 text-xs"
      disabled={disabled}
      onClick={onToggle}
      aria-expanded={open}
    >
      {open ? (
        <CaretDownIcon className="h-3 w-3" />
      ) : (
        <CaretRightIcon className="h-3 w-3" />
      )}
      {filled > 0
        ? gt('{filled} of {total} branches set', {
            context: 'branch expand toggle when some branches have a value',
            filled: String(filled),
            total,
          })
        : gt('{total} branches', {
            context: 'branch expand toggle when no branch has a value',
            total,
          })}
    </Button>
  );
}

/** One percentage row: a division, or one branch of one. */
function EntryRow({
  entry,
  label,
  indented,
  selected,
  form,
  invalid,
  onSelect,
  children,
}: {
  entry: DivisionEntry;
  /** What the row shows — the division's title, or just the branch letter. */
  label: string;
  indented?: boolean;
  selected: boolean;
  form: PricelistDetailsForm;
  invalid: boolean;
  onSelect: (selected: boolean) => void;
  /** The Branches cell; branch rows leave it empty. */
  children?: React.ReactNode;
}) {
  const gt = useGT();
  const percent = form.values.percentByEntryKey[entry.key] ?? '';
  return (
    <TableRow className={indented ? 'bg-muted/20' : undefined}>
      <TableCell className="py-1">
        <Checkbox
          checked={selected}
          disabled={form.isSaving}
          onCheckedChange={(checked) => onSelect(checked === true)}
          aria-label={gt('Select {row}', {
            context: 'aria label for a price change row selection checkbox',
            row: entry.label,
          })}
        />
      </TableCell>
      <TableCell
        className={
          indented
            ? 'py-1 pl-8 font-mono text-sm text-muted-foreground'
            : 'py-1 text-sm font-medium'
        }
      >
        {label}
      </TableCell>
      <TableCell className="py-1">
        <PercentInput
          ariaLabel={entry.label}
          value={percent}
          showSuffix={!isNotApplicable(percent)}
          acceptsText
          invalid={invalid}
          disabled={form.isSaving}
          className="w-24"
          onChange={(percent) => form.setEntryPercent(entry.key, percent)}
        />
      </TableCell>
      <TableCell className="py-1">{children}</TableCell>
    </TableRow>
  );
}

/**
 * The recorded price change per division, as a table.
 *
 * Every division is a row whether or not it has a value — a blank percentage is
 * simply not recorded (not reviewed yet), while N/A records that the change
 * doesn't apply (GEN-2576) — so recording a change across many divisions is a
 * select-and-apply rather than adding a field at a time. Divisions that price
 * per branch (GEN-2325) expand into a row per branch, each with its own value.
 */
export function PricelistDivisionTable({
  form,
}: {
  form: PricelistDetailsForm;
}) {
  const gt = useGT();
  const rows = divisionRows(form.divisions, form.values);
  const invalidKeys = new Set(form.invalidKeys);

  // Divisions with a per-branch percentage already saved open on entry, so
  // those values are visible without going looking for them.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set(rowsWithBranchPercents(rows, form.values)),
  );
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkPercent, setBulkPercent] = useState('');

  const visible = visibleEntryKeys(rows, expanded);
  const selectedCount = visible.filter((key) => selected.has(key)).length;
  const headerChecked =
    selectedCount === 0
      ? false
      : selectedCount === visible.length
        ? true
        : 'indeterminate';

  function toggleExpanded(row: DivisionRow, open: boolean) {
    setExpanded((prev) => withKey(prev, row.division.id, open));
    if (!open) {
      // A collapsed branch row can't be seen, and a selection the user can't
      // see would make the bulk count describe rows they aren't looking at.
      setSelected((prev) => {
        const next = new Set(prev);
        for (const branch of row.branches) next.delete(branch.key);
        return next;
      });
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {selectedCount > 0 && (
        <BulkApplyBar
          count={selectedCount}
          percent={bulkPercent}
          disabled={form.isSaving}
          onPercentChange={setBulkPercent}
          onApply={() =>
            form.setEntryPercents([...selected], bulkPercent.trim())
          }
          onMarkNotApplicable={() =>
            form.setEntryPercents(
              [...selected],
              DIVISION_PERCENT_NOT_APPLICABLE,
            )
          }
          onClear={() => form.setEntryPercents([...selected], '')}
          onDeselect={() => setSelected(new Set())}
        />
      )}

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">
                <Checkbox
                  checked={headerChecked}
                  disabled={form.isSaving}
                  onCheckedChange={(checked) =>
                    setSelected(checked === true ? new Set(visible) : new Set())
                  }
                  aria-label={gt('Select all rows', {
                    context:
                      'aria label for the select-all checkbox in the price change table',
                  })}
                />
              </TableHead>
              <TableHead>
                <T context="column heading for the division in the price change table">
                  Division
                </T>
              </TableHead>
              <TableHead className="w-32">
                <T context="column heading for the price change percentage">
                  Change
                </T>
              </TableHead>
              <TableHead>
                <T context="column heading for a division's branch rows">
                  Branches
                </T>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const open = expanded.has(row.division.id);
              return (
                <Fragment key={row.division.id}>
                  <EntryRow
                    entry={row.entry}
                    label={row.division.title}
                    selected={selected.has(row.entry.key)}
                    form={form}
                    invalid={invalidKeys.has(row.entry.key)}
                    onSelect={(isSelected) =>
                      setSelected((prev) =>
                        withKey(prev, row.entry.key, isSelected),
                      )
                    }
                  >
                    <BranchesCell
                      row={row}
                      values={form.values}
                      open={open}
                      disabled={form.isSaving}
                      onToggle={() => toggleExpanded(row, !open)}
                    />
                  </EntryRow>
                  {open &&
                    row.branches.map((branch) => (
                      <EntryRow
                        key={branch.key}
                        entry={branch}
                        label={branch.branchCode ?? ''}
                        indented
                        selected={selected.has(branch.key)}
                        form={form}
                        invalid={invalidKeys.has(branch.key)}
                        onSelect={(isSelected) =>
                          setSelected((prev) =>
                            withKey(prev, branch.key, isSelected),
                          )
                        }
                      />
                    ))}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
